import { useCachedQuery } from "@pustak-lms/ui";
import { Fragment, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import type { PlatformOrganization } from "@pustak-lms/contracts";
import { Button, PageHeader, InvitationList } from "@pustak-lms/ui";
import { getCountryName } from "@pustak-lms/utils";
import { platformApiClient } from "./api";

const pageSize = 10;

function dateLabel(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(date);
}

function detail(label: string, value: string | null) {
  return <div key={label}><dt>{label}</dt><dd>{value || "Not provided"}</dd></div>;
}

function OrganizationDetails({ organization }: { organization: PlatformOrganization }) {
  return (
    <div className="console-details">
      <dl>
        {detail("Slug", organization.slug)}
        {detail("Status", organization.status)}
        {detail("Owner", organization.ownerName)}
        {detail("Owner email", organization.ownerEmail)}
        {detail("Primary mobile", organization.ownerMobilePrimary)}
        {detail("Alternate mobile", organization.ownerMobileAlternate)}
        {detail("Organization email", organization.organizationEmail)}
        {detail("Website", organization.websiteUrl)}
        {detail("City", organization.city)}
        {detail("State or region", organization.stateRegion)}
        {detail("Country", getCountryName(organization.countryCode))}
        {detail("Internal notes", organization.internalNotes)}
        {detail("Created", dateLabel(organization.createdAt))}
      </dl>
      <InvitationList endpoint={`/api/platform/organizations/${organization.id}/invitations`} />
    </div>
  );
}

export function OrganizationsPage() {
  const location = useLocation();
  const deliveryStatus = (location.state as { deliveryStatus?: string } | null)?.deliveryStatus;
  const createdName = (location.state as { createdName?: string } | null)?.createdName;
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState<string | null>(null);
  const [previousCursors, setPreviousCursors] = useState<Array<string | null>>([]);
  const organizationsQuery = useCachedQuery(['organizations', { query, cursor, limit: pageSize }], () => platformApiClient().listOrganizations({ query, limit: pageSize, ...(cursor ? { cursor } : {}) }));
  const result = organizationsQuery.data;
  const loading = organizationsQuery.isPending;
  const error = organizationsQuery.error ? 'Unable to load organizations. Try again.' : null;
  const [expandedId, setExpandedId] = useState<string | null>(null);

  function search(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (query === searchInput.trim() && !cursor) void organizationsQuery.refetch();
    setQuery(searchInput.trim());
    setCursor(null);
    setPreviousCursors([]);
    setExpandedId(null);
  }

  function nextPage() {
    if (!result?.nextCursor) return;
    setPreviousCursors((previous) => [...previous, cursor]);
    setCursor(result.nextCursor);
    setExpandedId(null);
  }

  function previousPage() {
    if (previousCursors.length === 0) return;
    setCursor(previousCursors[previousCursors.length - 1] ?? null);
    setPreviousCursors((previous) => previous.slice(0, -1));
    setExpandedId(null);
  }

  return (
    <>
      <PageHeader
        eyebrow="Platform directory"
        title="Organizations"
        description="Find organizations and inspect their owner's contact details."
        actions={<Link className="button button-primary" to="/organizations/new">Create organization</Link>}
      />
      {createdName ? <div className="console-alert console-alert-success" role="status">{createdName} was created. {deliveryStatus ? `Owner invitation delivery: ${deliveryStatus}. Open the organization details to review or resend.` : ""}</div> : null}
      <section className="panel" aria-label="Organization list">
        <form className="console-search" onSubmit={search} role="search">
          <input
            aria-label="Search organizations"
            value={searchInput}
            maxLength={100}
            placeholder="Search organization or owner"
            onChange={(event) => setSearchInput(event.target.value)}
          />
          <Button type="submit" variant="secondary">Search</Button>
        </form>

        {loading ? <p className="console-empty" role="status">Loading organizations…</p> : null}
        {!loading && error ? (
          <div className="console-empty" role="alert">
            <p>{error}</p>
            <Button variant="secondary" onClick={() => void organizationsQuery.refetch()}>Retry</Button>
          </div>
        ) : null}
        {!loading && !error && result?.items.length === 0 ? (
          <p className="console-empty">{query ? "No organizations match this search." : "No organizations yet. Create the first one."}</p>
        ) : null}
        {!loading && !error && result && result.items.length > 0 ? (
          <>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr><th>Organization</th><th>Owner</th><th>Email</th><th>Primary mobile</th><th>Country</th><th>Created</th><th>Appearance</th><th>Details</th></tr>
                </thead>
                <tbody>
                  {result.items.map((organization) => (
                    <Fragment key={organization.id}>
                      <tr>
                        <td><strong>{organization.name}</strong></td>
                        <td>{organization.ownerName}</td>
                        <td>{organization.ownerEmail}</td>
                        <td>{organization.ownerMobilePrimary}</td>
                        <td>{getCountryName(organization.countryCode)}</td>
                        <td>{dateLabel(organization.createdAt)}</td>
                        <td><Link className="console-appearance-link" to={`/organizations/${organization.id}/appearance`} aria-label={`Student appearance for ${organization.name}`}>Student appearance</Link></td>
                        <td>
                          <button
                            className="console-detail-button"
                            type="button"
                            aria-expanded={expandedId === organization.id}
                            onClick={() => setExpandedId(expandedId === organization.id ? null : organization.id)}
                          >{expandedId === organization.id ? "Hide" : "View"}</button>
                        </td>
                      </tr>
                      {expandedId === organization.id ? (
                        <tr><td colSpan={8}><OrganizationDetails organization={organization} /></td></tr>
                      ) : null}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="console-list-actions">
              <span className="console-muted">{result.total} {result.total === 1 ? "organization" : "organizations"}</span>
              <div className="console-pagination">
                <Button variant="secondary" disabled={previousCursors.length === 0} onClick={previousPage}>Previous</Button>
                <Button variant="secondary" disabled={!result.nextCursor} onClick={nextPage}>Next</Button>
              </div>
            </div>
          </>
        ) : null}
      </section>
    </>
  );
}
